import { KeyRound, Lock, ShieldCheck, Vault, Smartphone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES, SITE } from "@/lib/siteConfig";

const ICONS = {
  ouverture: KeyRound,
  depannage: Lock,
  blindage: ShieldCheck,
  coffre: Vault,
  connectee: Smartphone,
};

const SERVICE_LINKS = {
  ouverture: "/ouverture-de-porte",
  depannage: "/depannage-serrurier",
  blindage: "/blindage-de-porte",
  coffre: "/depannage-coffre-fort",
  connectee: "/installation-serrure-connectee",
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-white py-20 sm:py-28 lg:py-32 reveal"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-600 mb-4">
            — Nos services
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] uppercase leading-[0.95]">
            Une seule équipe.<br />Tous vos problèmes de serrure.
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            De l'ouverture simple au blindage complet, nos techniciens interviennent avec du
            matériel certifié et un devis clair avant toute action.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5">
          {/* Big card - Ouverture (poster) */}
          <ServiceCard service={SERVICES[0]} className="md:col-span-8 md:row-span-2 min-h-[440px] bg-[#0A0A0A] text-white" dark large />
          {/* Medium - Serrure */}
          <ServiceCard service={SERVICES[1]} className="md:col-span-4 min-h-[210px] bg-red-600 text-white" red />
          {/* Medium - Blindage */}
          <ServiceCard service={SERVICES[2]} className="md:col-span-4 min-h-[210px] bg-[#F3F4F6]" />
          {/* Coffre */}
          <ServiceCard service={SERVICES[3]} className="md:col-span-6 min-h-[210px] bg-[#F3F4F6]" />
          {/* Urgence */}
          <ServiceCard service={SERVICES[4]} className="md:col-span-6 min-h-[210px] bg-[#0A0A0A] text-white" dark />
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-[#F3F4F6] border-l-4 border-red-600 p-6 sm:p-8">
          <div>
            <div className="font-display font-black text-xl sm:text-2xl tracking-tight">
              Besoin d'un conseil immédiat ?
            </div>
            <div className="text-slate-600">
              Appelez-nous, on vous répond en moins d'une sonnerie.
            </div>
          </div>
          <a
            href={SITE.phoneHref}
            data-testid="call-cta-services"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-4 font-display font-bold uppercase tracking-tight transition-all shadow-[0_4px_0_#991B1B] hover:shadow-[0_2px_0_#991B1B] hover:-translate-y-0.5"
          >
            {SITE.phone}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, className = "", dark = false, red = false, large = false }) {
  const Icon = ICONS[service.key];
  const accent = dark ? "text-red-400" : red ? "text-white" : "text-red-600";
  const sub = dark ? "text-slate-300" : red ? "text-red-50" : "text-slate-600";
  const link = SERVICE_LINKS[service.key];
  const isPoster = service.poster === true && large;

  // Poster mode: full image visible, no text overlay (image is self-branded)
  if (isPoster) {
    return (
      <Link
        to={link}
        data-testid={`service-card-${service.key}`}
        className={`relative group flex items-stretch justify-center overflow-hidden border border-black/10 bg-[#0A0A0A] transition-all hover:-translate-y-1 ${className}`}
      >
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-contain object-center group-hover:scale-[1.02] transition-transform duration-700"
        />
        <div className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg group-hover:gap-2.5 transition-all">
          En savoir plus
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={link}
      data-testid={`service-card-${service.key}`}
      className={`relative group p-6 sm:p-8 flex flex-col justify-between border border-black/10 overflow-hidden transition-all hover:-translate-y-1 ${className}`}
    >
      {large && service.image && (
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/20" />
        </div>
      )}
      <div className="relative z-10 flex items-start justify-between">
        <div className={`inline-flex items-center justify-center w-12 h-12 ${red ? "bg-white/15" : dark ? "bg-white/10" : "bg-white"} border ${red || dark ? "border-white/20" : "border-black/10"}`}>
          <Icon className={`w-6 h-6 ${red ? "text-white" : dark ? "text-red-400" : "text-red-600"}`} strokeWidth={2} />
        </div>
        <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${accent}`}>
          {service.price}
        </span>
      </div>

      <div className="relative z-10 mt-auto pt-10">
        <h3 className={`font-display font-black uppercase tracking-tight ${large ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"}`}>
          {service.title}
        </h3>
        <p className={`mt-3 text-sm sm:text-base ${sub} max-w-md`}>
          {service.desc}
        </p>
        <div className={`mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] ${accent} group-hover:gap-2.5 transition-all`}>
          En savoir plus
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
